import { lineFor, saidFor, type Example, type WelcomeStep } from "../app/welcome-model.js";
import { END, STILL, STILL_TAP, dropDy, swipeAt, tapAt, type LessonFrame } from "../app/welcome-lesson.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { markOf, type Mark } from "./aim.js";
import type { SheetHistory } from "./sheet.js";

/**
 * DCTA-216 (docs/design/v3/10-welcome.md, mock-up 24-welcome.html, vault.css §14) — the first-run welcome: one layer over
 * Home (or over Me, opened from Help). The app dims, the real mark stays lit above the dim (`#app[data-wl="open"]` lifts
 * it to z 6 over the layer's z 5) and live, and one panel above the mark's lift track teaches two or three steps in one
 * line each: Back · "1 of 3" · Skip on top, the step's line and example, Next in the same place on every step.
 *
 * Not a `<dialog>` (the top layer would cover the mark): a `role="dialog" aria-modal` region with the app behind made
 * inert by hand — the top bar and the body, each tab — while the mark leaves the tab order but keeps its finger. Focus
 * starts on Next and stays inside the panel. Step 1's lesson paints the real mark frame by frame (`Mark.demo`), never
 * calling its gesture machine and never starting the microphone; a real finger takes the mark at once. Every way out —
 * Done, Skip, Esc, the system Back, the lit mark, the session ending — closes it the same way and says which (`onClose`).
 * No network call, no speech, no sound.
 */
export type WelcomeEnd = "done" | "skip" | "esc" | "back" | "mark" | "session";

export interface WelcomeClock {
  now(): number;
  frame(fn: () => void): void;
  later(fn: () => void, ms: number): number;
  clear(id: number): void;
}

export const browserWelcomeClock = (): WelcomeClock => ({
  now: () => performance.now(),
  frame: (fn) => void requestAnimationFrame(() => fn()),
  later: (fn, ms) => window.setTimeout(fn, ms),
  clear: (id) => window.clearTimeout(id),
});

export interface WelcomeDeps {
  /** `#app`: the layer goes in the screen's own stacking context, beside the dock. */
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  /** The system's Back closes it (one history entry, `lib/nav.ts`). */
  readonly history?: SheetHistory;
  readonly clock?: WelcomeClock;
  readonly onClose: (end: WelcomeEnd) => void;
}

export interface WelcomeOpen {
  readonly steps: readonly WelcomeStep[];
  readonly example: Example;
  /** No permission, no engine or offline: step 1 teaches the tap only (W3b). */
  readonly voiceOff: boolean;
  /** The screen under it: its regions go inert; the mark stays live. */
  readonly screen: { readonly parts: readonly HTMLElement[]; readonly mark: HTMLButtonElement };
  /** Where focus goes back on Done, Skip, Esc and Back: the mark (Home) or the "How it works" row (Me). */
  readonly returnTo: HTMLElement;
}

/** The step's words arriving (§2 step 2): from 200 ms, 90 ms a word, as a live transcript's do. */
const WORD_FROM = 200;
const WORD_EVERY = 90;
/** The layer's exit: the panel fades and drops 12 px, the dim fades (§0.5). */
const EXIT_MS = 200;
let ids = 0;

interface Open {
  readonly o: WelcomeOpen;
  readonly mark: Mark | undefined;
  readonly layer: HTMLElement;
  readonly pnl: HTMLElement;
  readonly back: HTMLButtonElement;
  readonly of: HTMLElement;
  readonly skip: HTMLButtonElement;
  readonly next: HTMLButtonElement;
  readonly said: HTMLElement;
  readonly hid: (readonly [HTMLElement, string | null])[];
  readonly entry: number | undefined;
  readonly lineId: string;
  /** Takes the lesson's listener off the mark again. */
  unbind: () => void;
  step: number;
  /** The lesson's start (clock ms) — undefined when no lesson is playing. */
  lessonAt: number | undefined;
  lessonRun: number;
  typing: number[];
  ending: boolean;
}

export class WelcomeLayer {
  readonly #d: WelcomeDeps;
  readonly #clock: WelcomeClock;
  #open: Open | undefined;
  #onKey = (e: KeyboardEvent): void => this.#key(e);
  #onResize = (): void => this.#place();

  constructor(deps: WelcomeDeps) {
    this.#d = deps;
    this.#clock = deps.clock ?? browserWelcomeClock();
  }

  get isOpen(): boolean {
    return this.#open !== undefined && !this.#open.ending;
  }

  open(o: WelcomeOpen): void {
    if (this.#open !== undefined) this.#finish(this.#open);
    const n = String(++ids);
    const ofId = `wl-of-${n}`;
    const lineId = `wl-h-${n}`;
    const back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back" } }, [icon("i-chev-l")]);
    const of = el("span", { class: "of", id: ofId });
    const skip = el("button", { class: "raw skip", type: "button" }, ["Skip"]);
    const next = el("button", { class: "raw btn next", type: "button" }, ["Next"]);
    const said = el("span", { class: "vis-hidden said", role: "status", aria: { atomic: "true" } });
    const pnl = el("div", { class: "pnl" }, [el("div", { class: "top" }, [back, of, skip]), el("div", { class: "cue" }), el("div", { class: "go" }, [next]), said]);
    const layer = el("div", { class: "wl", role: "dialog", aria: { modal: "true", label: "How it works", describedby: `${ofId} ${lineId}` } }, [pnl]);

    // The app behind: inert and hidden from assistive tech; the mark out of the tab order, live for a finger.
    const hid: (readonly [HTMLElement, string | null])[] = [];
    const hide = (e: HTMLElement): void => {
      hid.push([e, e.getAttribute("aria-hidden")]);
      e.setAttribute("aria-hidden", "true");
      e.inert = true;
    };
    for (const part of o.screen.parts) {
      if (part.contains(o.screen.mark)) {
        const nav = o.screen.mark.closest("nav");
        if (nav !== null) {
          hid.push([nav, nav.getAttribute("aria-hidden")]);
          nav.setAttribute("aria-hidden", "true");
        }
        for (const tab of part.querySelectorAll<HTMLElement>(".tab")) tab.inert = true;
      } else hide(part);
    }
    o.screen.mark.setAttribute("tabindex", "-1");

    const state: Open = {
      o,
      mark: markOf(o.screen.mark),
      layer,
      pnl,
      back,
      of,
      skip,
      next,
      said,
      hid,
      entry: this.#d.history?.push(() => {
        if (this.#open !== state || state.ending) return true;
        this.#close(state, "back", false, true);
        return true;
      }),
      lineId,
      unbind: () => undefined,
      step: 0,
      lessonAt: undefined,
      lessonRun: 0,
      typing: [],
      ending: false,
    };
    this.#open = state;

    next.addEventListener("click", () => {
      if (state.ending) return;
      if (state.step < o.steps.length - 1) this.#go(state, state.step + 1);
      else this.#close(state, "done", true, false);
    });
    back.addEventListener("click", () => {
      if (!state.ending && state.step > 0) this.#go(state, state.step - 1);
    });
    skip.addEventListener("click", () => {
      if (!state.ending) this.#close(state, "skip", true, false);
    });
    // A real finger on the lit mark: the lesson stops at once (the mark ends its own demo in the same capture phase).
    const stop = (): void => this.#stopLesson(state);
    o.screen.mark.addEventListener("pointerdown", stop, { capture: true });
    state.unbind = () => o.screen.mark.removeEventListener("pointerdown", stop, { capture: true });
    pnl.addEventListener("keydown", (e) => this.#trap(state, e));

    this.#d.host.dataset["wl"] = "open";
    this.#d.host.append(layer);
    document.addEventListener("keydown", this.#onKey);
    window.addEventListener("resize", this.#onResize);
    this.#draw(state, false);
    this.#place();
    next.focus({ preventScroll: true });
  }

  /** Close it from outside (the session ended, another screen came). Nothing happens if it is not open. */
  close(end: WelcomeEnd): void {
    const s = this.#open;
    if (s === undefined || s.ending) return;
    this.#close(s, end, end !== "mark" && end !== "session", false);
  }

  /**
   * The lit mark was used: closed (seen), and its history entry is HANDED OVER to what the mark opens — the chat takes
   * it (`Nav.swap`). Consuming it here would be a `history.back()` that lands after the chat's own entry and closes the
   * chat (lib/nav.ts). Undefined when it was not open.
   */
  handOver(): number | undefined {
    const s = this.#open;
    if (s === undefined || s.ending) return undefined;
    this.#close(s, "mark", false, true);
    return s.entry;
  }

  #go(s: Open, step: number): void {
    const from = s.pnl.getBoundingClientRect().height;
    s.step = step;
    this.#draw(s, true);
    if (step === 0 && document.activeElement !== s.next) s.next.focus({ preventScroll: true });
    // The panel's height tweens old → new (§0.5); the anchor never moves, so Next does not either.
    const to = s.pnl.getBoundingClientRect().height;
    if (!this.#d.reduced() && typeof s.pnl.animate === "function" && Math.abs(to - from) > 1) {
      s.pnl.animate([{ height: `${String(from)}px` }, { height: `${String(to)}px` }], { duration: 200, easing: "cubic-bezier(.2,.8,.2,1)" });
    }
  }

  /** One step: the count, the corners, Next's word, a fresh cue (its arrival runs once), the lesson or the example. */
  #draw(s: Open, announce: boolean): void {
    const { o } = s;
    const kind = o.steps[s.step] ?? "start";
    const last = s.step === o.steps.length - 1;
    s.of.textContent = `${String(s.step + 1)} of ${String(o.steps.length)}`;
    s.back.dataset["off"] = s.step === 0 ? "true" : "false";
    s.skip.dataset["off"] = last ? "true" : "false";
    s.next.textContent = last ? "Done" : "Next";
    const line = lineFor(kind, o.voiceOff);
    const h2 = el("h2", { id: s.lineId, tabIndex: -1 }, kind === "start" ? sentences(line) : [line]);
    const vis = visual(kind, o.example);
    const cue = el("div", { class: "cue" }, [h2, ...(vis === null ? [] : [vis])]);
    s.pnl.querySelector(".cue")?.replaceWith(cue);
    this.#stopTyping(s);
    if (kind === "start") this.#lesson(s);
    else this.#rest(s);
    if (kind === "say") this.#typeIn(s, cue);
    if (announce) s.said.textContent = saidFor(s.step, o.steps.length, kind, o.voiceOff, o.example);
  }

  #lesson(s: Open): void {
    const run = ++s.lessonRun;
    const at = s.o.voiceOff ? tapAt : swipeAt;
    const still = s.o.voiceOff ? STILL_TAP : STILL;
    const end = s.o.voiceOff ? END.tap : END.swipe;
    if (this.#d.reduced()) {
      this.#paint(s, still);
      s.lessonAt = undefined;
      return;
    }
    const t0 = this.#clock.now();
    s.lessonAt = t0;
    const tick = (): void => {
      if (this.#open !== s || s.lessonRun !== run || s.lessonAt === undefined) return;
      const t = this.#clock.now() - t0;
      if (t >= end) {
        this.#paint(s, still);
        s.lessonAt = undefined;
        return;
      }
      this.#paint(s, at(t));
      this.#clock.frame(tick);
    };
    this.#paint(s, at(0));
    this.#clock.frame(tick);
  }

  #paint(s: Open, f: LessonFrame): void {
    s.mark?.demo({ state: f.state, p: f.p, dy: dropDy(f), gy: f.gy, go: f.go, gs: f.gs });
  }

  #stopLesson(s: Open): void {
    s.lessonRun += 1;
    s.lessonAt = undefined;
  }

  #rest(s: Open): void {
    this.#stopLesson(s);
    s.mark?.demo(null);
  }

  /** Step 2's sentence arrives word by word (opacity only: the whole sentence is in the DOM from the first frame). */
  #typeIn(s: Open, cue: HTMLElement): void {
    const me = cue.querySelector<HTMLElement>(".well .me");
    if (me === null) return;
    const words = [...me.querySelectorAll<HTMLElement>(".w")];
    if (this.#d.reduced()) {
      me.dataset["typing"] = "false";
      return;
    }
    me.dataset["typing"] = "true";
    s.typing = words.map((w, i) =>
      this.#clock.later(() => {
        w.classList.add("on");
        if (i === words.length - 1) me.dataset["typing"] = "false";
      }, WORD_FROM + i * WORD_EVERY),
    );
  }

  #stopTyping(s: Open): void {
    for (const id of s.typing) this.#clock.clear(id);
    s.typing = [];
  }

  /** The panel's anchor from the mark's own box: its bottom edge --wl-gap above the lift track; the tail at the mark. */
  #place(): void {
    const s = this.#open;
    if (s === undefined) return;
    const host = this.#d.host.getBoundingClientRect();
    const mark = s.o.screen.mark.getBoundingClientRect();
    if (host.height === 0 || mark.height === 0) return; // not laid out (a test page)
    const drag = parseFloat(getComputedStyle(s.o.screen.mark).getPropertyValue("--aim-drag")) || 96;
    const gap = parseFloat(getComputedStyle(s.layer).getPropertyValue("--wl-gap")) || 20;
    const liftTop = mark.bottom - host.top - (drag + 62);
    s.layer.style.setProperty("--pb", `${(host.height - (liftTop - gap)).toFixed(1)}px`);
    s.layer.style.setProperty("--tx", `${(mark.left + mark.width / 2 - host.left - 12).toFixed(1)}px`);
  }

  #key(e: KeyboardEvent): void {
    const s = this.#open;
    if (s === undefined || s.ending || e.key !== "Escape") return;
    e.preventDefault();
    this.#close(s, "esc", true, false);
  }

  /** Back → Skip → Next, and around: the app behind is inert, the panel is the only place focus goes. */
  #trap(s: Open, e: KeyboardEvent): void {
    if (e.key !== "Tab") return;
    const order = [s.back, s.skip, s.next].filter((b) => b.dataset["off"] !== "true");
    const at = order.indexOf(document.activeElement as HTMLButtonElement);
    const to = e.shiftKey ? (at <= 0 ? order.length - 1 : at - 1) : at === -1 || at === order.length - 1 ? 0 : at + 1;
    e.preventDefault();
    order[to]?.focus({ preventScroll: true });
  }

  /** `keepEntry`: the history entry is not consumed — the system's Back already popped it, or the chat takes it over. */
  #close(s: Open, end: WelcomeEnd, refocus: boolean, keepEntry: boolean): void {
    s.ending = true;
    this.#stopTyping(s);
    this.#rest(s);
    s.unbind();
    document.removeEventListener("keydown", this.#onKey);
    window.removeEventListener("resize", this.#onResize);
    if (s.entry !== undefined && !keepEntry) this.#d.history?.done(s.entry);
    // The app comes back at once: what is behind is usable while the layer leaves.
    for (const [e, was] of s.hid) {
      if (was === null) e.removeAttribute("aria-hidden");
      else e.setAttribute("aria-hidden", was);
      e.inert = false;
    }
    for (const tab of this.#d.host.querySelectorAll<HTMLElement>(".tabs .tab")) tab.inert = false;
    s.o.screen.mark.removeAttribute("tabindex");
    delete this.#d.host.dataset["wl"];
    if (refocus) s.o.returnTo.focus({ preventScroll: true });
    this.#d.onClose(end);
    if (this.#d.reduced() || typeof s.pnl.animate !== "function") {
      this.#finish(s);
      return;
    }
    const ease = getComputedStyle(document.documentElement).getPropertyValue("--ease-in").trim() || "cubic-bezier(.4,0,1,1)";
    s.pnl.animate([{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateY(12px)" }], { duration: EXIT_MS, easing: ease, fill: "forwards" });
    s.layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: EXIT_MS, easing: "linear", fill: "forwards" });
    this.#clock.later(() => this.#finish(s), EXIT_MS + 20);
  }

  #finish(s: Open): void {
    s.layer.remove();
    if (this.#open === s) this.#open = undefined;
  }
}

/** Step 1's line as sentence units: a two-sentence line breaks between them, never inside one (§0.4). */
function sentences(line: string): (Node | string)[] {
  const parts = line.split(/(?<=\.) /);
  return parts.flatMap((p, i) => [...(i > 0 ? [" "] : []), el("span", { class: "s" }, [p])]);
}

/** The step's example: the customer's own sentence (step 2), or the card the bank builds from it (step 3). */
function visual(kind: WelcomeStep, example: Example): HTMLElement | null {
  if (kind === "say") {
    const words = example.say.split(" ").flatMap((w, i) => [...(i > 0 ? [" "] : []), el("span", { class: "w" }, [w])]);
    return el("div", { class: "vis" }, [
      el("p", { class: "eg" }, ["For example"]),
      el("div", { class: "well" }, [el("div", { class: "me", data: { typing: "false" } }, [el("p", {}, words)])]),
    ]);
  }
  const card = example.card;
  if (kind !== "check" || card === undefined) return null;
  return el("div", { class: "vis" }, [
    el("p", { class: "eg" }, ["For example"]),
    el("div", { class: "well" }, [
      el("div", { class: "bank ex", role: "group", aria: { label: "Example of the bank's card" } }, [
        el("div", { class: "bh" }, [el("span", { class: "banktag" }, ["Bank"]), el("b", { class: "count" }, [card.count]), el("span", { class: "money total" }, [card.total])]),
        el(
          "div",
          { class: "rows" },
          card.ops.map((op) =>
            el("div", { class: "op" }, [
              el("span", { class: "ic", aria: { hidden: "true" } }, [icon(op.icon)]),
              el("span", { class: "tx" }, [el("b", {}, [op.title]), el("small", {}, [op.sub])]),
              el("span", { class: "amt money" }, [op.amount]),
            ]),
          ),
        ),
        el("div", { class: "bf" }, [el("div", { class: "btn wsign" }, [icon("i-key"), el("span", { class: "lbl" }, [card.sign])])]),
      ]),
    ]),
  ]);
}
